import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AddQuickSelectionDialogComponentComponent } from '@fi-shared/add-quick-selection-dialog-component/add-quick-selection-dialog-component.component';
import { AccessRightService } from '@fi-shared/fi-shared-services/access-right.service';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
    signal,
} from '@angular/core';
import { PostModel } from '@fi-shared/models';
import { FiNewsFeedEditMobileService } from '../edit-mobile/edit-mobile.service';
import { ModuleType } from '@fi-shared/types/module-type';
import { NewsFeedModuleService } from '../news-feed-module.service';
import { AccessRightFiltersComponent } from '@fi-shared/access-right-filters/access-right-filters.component';
import { FiSnackBarService, PostService } from '@fi-shared/fi-shared-services';
import { ITextEditorLinkAttachment } from '@fi-shared/fi-text-editor/fi-text-editor';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { TextEditorService } from '@fi-shared/fi-text-editor/text-editor.service';
import { fiDebounce } from '@fi-shared/utils';
import { AccessRightFilterSettings } from '@fi-shared/access-right-filters/access-right-filter-settings';
import { FiDialogCloseReason } from '@fi-shared/fi-dialogs/fi-dialogs-close-reason';
import { FiContenteditableService } from '@fi-shared/fi-contenteditable/fi-contenteditable.service';
import { takeUntil } from 'rxjs/operators';
import { ObjectTypes } from '@fi-shared/types';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { TextEditorComponent } from '@fi-shared/text-editor/text-editor.component';
import { FiUserSettingType } from '@fi-shared/types/fi-user-setting-type';
import { TextEditorAttachmentType } from '@fi-shared/fi-text-editor/fi-text-editor-attachment/text-editor-attachment-type';
import { FiAttachmentsListComponent } from '@fi-shared/media-bank/fi-attachments/fi-attachments-list.component';
import { TextEditorInsertEmbedVideoService } from '@fi-shared/fi-text-editor/fi-text-editor-insert-embed-video/text-editor-insert-embed-video.service';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';
import { FiTextEditorComponent } from '@fi-app/shared/fi-text-editor/fi-text-editor/fi-text-editor.component';

@Component({
    selector: 'fi-news-feed-item-edit',
    templateUrl: './news-feed-item-edit.component.html',
    styleUrls: ['./news-feed-item-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-news-feed-item-edit' },
    standalone: false,
})
export class NewsFeedItemEditComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input('editorPost') EditorPost: PostModel;
    @Input('accessRightFilterSettings') AccessRightFilterSettings: AccessRightFilterSettings;
    @Input() placeholder;
    @Input() noControls: boolean;
    @Output() afterSave = new EventEmitter<PostModel>();
    @Output() editorOnFocus = new EventEmitter();
    @Output() onCancelPostEdit = new EventEmitter<void>();
    @ViewChild(FiTextEditorComponent, { static: false }) textEditorComponent: FiTextEditorComponent;
    // @ViewChild(TextEditorComponent, { static: false }) textEditorComponent: TextEditorComponent;
    @ViewChild(AccessRightFiltersComponent, { static: false }) accessRightFilters: AccessRightFiltersComponent;
    @ViewChild(FiAttachmentsListComponent, { static: false }) attachmentsListComponent: FiAttachmentsListComponent;

    protected displayEditorActions = signal(false);

    PostSaving: boolean = false;
    quickSelectionList;
    isNewQuickSelectionFocus: boolean;
    hideTooltip$ = new Subject<void>();

    constructor(
        private textEditorService: TextEditorService,
        private newsFeedEditMobileService: FiNewsFeedEditMobileService,
        private newsFeedModuleService: NewsFeedModuleService,
        private cdr: ChangeDetectorRef,
        private accessRightService: AccessRightService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private contenteditableService: FiContenteditableService,
        private snackbarService: FiSnackBarService,
        public elementRef: ElementRef,
        private textEditorInsertEmbedVideoService: TextEditorInsertEmbedVideoService,
        private fileUpload2Service: FiFileUpload2Service,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    ngOnInit(): void {
        this.checkShowAccessRightFilters();
    }

    uploadFiles(files: File[]) {
        this.fileUpload2Service.silentUploadFiles(files).then((mediaBankReferences: IMediaBankFileReference[]) => {
            if (!this.EditorPost.MediaBankFileReferences?.length) {
                this.EditorPost.MediaBankFileReferences = [];
            }
            this.EditorPost.MediaBankFileReferences.push(...mediaBankReferences);
            this.cdr.markForCheck();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        let change = changes.noControls;
        if (change) {
            this.checkShowAccessRightFilters();
        }
    }

    ngAfterViewInit(): void {
        if (this.EditorPost.Id) {
            // set cursor to end when edit existing post
            this.contenteditableService.setCursorPosition(this.textEditorComponent.editorElement.nativeElement);
        }
    }

    protected onFocus(): void {
        this.displayEditorActions.set(true);
        this.editorOnFocus.emit();
    }

    showAccessRightFilters$ = new ReplaySubject<boolean>();
    private checkShowAccessRightFilters() {
        if (this.postEditDialogOpened$.value || this.EditorPost.IsForumPost) return;

        this.showAccessRightFilters$.next(!this.noControls);
    }

    getModuleType(post: PostModel) {
        return post.ForumId ? ModuleType.ForumPost : ModuleType.Post;
    }

    getObjectType(post: PostModel): ObjectTypes {
        return post.ForumId ? ObjectTypes.ForumPost : ObjectTypes.Post;
    }

    getSearchObjectId(post: PostModel) {
        return post.ForumId || null;
    }

    isSaveDisabled() {
        return (
            this.PostSaving ||
            (!this.EditorPost.Content.trim().length &&
                !this.EditorPost.MediaBankFileReferences.length &&
                !this.EditorPost.Links.length)
        );
    }

    EditorSavePost() {
        this.accessRightService
            .GetSelectionFiltersForPublication(this.AccessRightFilterSettings.ModuleType, true)
            .then((selectionFiltersCollection) => {
                this.quickSelectionList = selectionFiltersCollection;
                this.checkQuickSelection();
            });
    }

    resetSelection$: Subject<void> = new Subject<void>();
    cancelPostEdit(): void {
        this.onCancelPostEdit.emit();
        this.EditorPost.Reset(this.fiFormatDateService);
        this.resetSelection$.next();
        this.displayEditorActions.set(false);
    }

    checkQuickSelection() {
        const isAccessRightFilterCollectionValid = this.accessRightFilters
            ? this.accessRightFilters.validateAccessRightFilterCollection()
            : true;

        if (isAccessRightFilterCollectionValid) {
            if (
                this.accessRightFilters &&
                !this.quickSelectionList.length &&
                !this.accessRightFilters.CreateSelectionFilterModel.Checked &&
                this.AccessRightFilterSettings.ModuleType === ModuleType.Post
            ) {
                this.fiDialogsService
                    .onboarding({
                        headerText: this.appTextService.get('new_quick_selection_dialog_header'),
                        yesButtonText: this.appTextService.get('access_rights.save_quick_selection'),
                        noButtonText: this.appTextService.get('common.skip'),
                        contentComponentRef: AddQuickSelectionDialogComponentComponent,
                        dontShowAgainProperty: FiUserSettingType.DontShowNewQuickSelectionMessage,
                        config: {
                            width: '580px',
                        },
                    })
                    .afterClosed((result) => {
                        if (result.reason === FiDialogCloseReason.YesButton) {
                            this.isNewQuickSelectionFocus = true;
                            this.cdr.markForCheck();
                        }
                        if (
                            result.reason === FiDialogCloseReason.NoButton ||
                            result.reason === FiDialogCloseReason.DontShowAgain
                        )
                            this.savePost();
                    });
            } else {
                this.savePost();
            }
        }
    }

    savePost() {
        this.PostSaving = true;
        this.newsFeedModuleService
            .savePost(this.EditorPost, this.accessRightFilters)
            .then((result) => {
                this.PostSaving = false;
                if (result) {
                    this.afterSave.emit(result);
                    this.cdr.markForCheck();
                }
                this.snackbarService.showSnackbar({
                    textMessage: this.appTextService.get('post.post_saved'),
                    buttonText: this.appTextService.get('common.dismiss'),
                });
            })
            .catch(() => {
                this.PostSaving = false;
            });
    }

    getElementId() {
        return this.EditorPost.Id
            ? 'NewsFeedPostEditor-' + this.EditorPost.Id + '-' + this.EditorPost.GetObjectType()
            : 'NewsFeedPostEditor';
    }

    // https://chainformation.atlassian.net/browse/FI-3624
    postEditDialogOpened$ = new BehaviorSubject<boolean>(false);
    postMobileTriggerClick() {
        this.postEditDialogOpened$.next(true);
        this.newsFeedEditMobileService
            .openMobilePostEditor(this.EditorPost)
            .afterClosed()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((result) => {
                if (result) {
                    this.EditorSavePost();
                } else {
                    this.EditorPost.Reset(this.fiFormatDateService);
                }
                this.postEditDialogOpened$.next(false);
            });
    }

    onNewAttachment($event: IMediaBankFileReference) {
        this.EditorPost.MediaBankFileReferences = [...this.EditorPost.MediaBankFileReferences, $event];
        this.onContainerHeightChanged();
    }

    onUpdateLinkAttachment($event: ITextEditorLinkAttachment) {
        // new link - add to Links
        const idx = this.EditorPost.Links.indexOf($event);
        if (idx === -1) this.EditorPost.Links = [...this.EditorPost.Links, $event];
    }

    onContentChange() {
        this.onContentChangeDebounced();
    }
    private onContentChangeDebounced = fiDebounce(() => {
        if (isNullOrUndefined(this.textEditorComponent)) {
            return;
        }
        this.textEditorService.updateAttachmentsFromContent(
            this.textEditorComponent.elementRef.nativeElement,
            this.EditorPost.Links,
        );
    }, 500);

    onContainerHeightChanged() {
        this.hideTooltip$.next();
    }

    protected onEditLinkAttachment($event: ITextEditorLinkAttachment) {
        if ($event.LinkType === TextEditorAttachmentType.Youtube) this.openInsertEmbedVideoDialog($event);
    }

    private openInsertEmbedVideoDialog(attachment?: ITextEditorLinkAttachment) {
        this.textEditorInsertEmbedVideoService.openInsertEmbedVideoDialog(attachment, {
            existingLinkAttachments: this.EditorPost.Links,
            onYesButtonClick: () => {
                this.cdr.markForCheck();
                this.attachmentsListComponent.emitDetectChanges(); // for proper attachments list update
            },
        });
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
